import type { ComponentProps, ReactNode } from "react";
import { tv } from "tailwind-variants";

import { IconWrapper } from "../common";
import { Label } from "./label";
import { Message } from "./message";

const inputVariants = tv({
  slots: {
    fieldContainer: "relative",
    inputContainer: "relative flex flex-row items-center",
    input: [
      "block w-full rounded-lg border bg-surface px-3 py-2 text-sm text-content shadow-raised",
      "border-line-control placeholder:text-content-subtle",
      "transition-[color,box-shadow,border-color] duration-150",
      "hover:border-content-subtle",
      "focus:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-focus-ring/40 focus-visible:outline-none",
      "disabled:cursor-not-allowed disabled:border-line-strong disabled:bg-surface-sunken disabled:text-content-subtle",
    ],
    /*
      Anchored to inputContainer, so a vertical margin on the control itself
      inflates this box and the affix centres against the margin rather than the
      field — put spacing on `containerClassName`, not `className`.
    */
    affix: "pointer-events-none absolute inset-y-0 flex items-center text-content-subtle",
  },
  variants: {
    error: {
      true: {
        input: "border-danger focus-visible:border-danger focus-visible:ring-danger/30",
      },
    },
    left: {
      true: { input: "pl-10" },
    },
    right: {
      true: { input: "pr-10" },
    },
  },
});

const { affix, fieldContainer, input, inputContainer } = inputVariants();

export type InputProps = {
  compact?: boolean;
  containerClassName?: string;
  wrapperClassName?: string;
  iconClassName?: string;
  labelClassName?: string;
  error?: string | boolean;
  id: string;
  label?: ReactNode;
  left?: ReactNode;
  right?: ReactNode;
  message?: string;
} & ComponentProps<"input">;

export const Input = ({
  className,
  compact = true,
  containerClassName,
  disabled,
  error,
  id,
  label,
  labelClassName,
  left,
  message,
  right,
  wrapperClassName,
  ...rest
}: InputProps) => {
  const hasError = Boolean(error);
  const messageId = `${id}-message`;
  const showMessage = !compact || Boolean(message) || hasError;

  return (
    <div className={fieldContainer({ className: containerClassName })}>
      {label ? (
        <Label
          className={labelClassName}
          disabled={disabled}
          htmlFor={id}
          label={label}
          required={rest.required}
        />
      ) : null}

      <div className={inputContainer({ className: wrapperClassName })}>
        {left ? (
          <div className={affix({ className: "left-0 pl-3" })}>
            <IconWrapper size="sm">{left}</IconWrapper>
          </div>
        ) : null}

        <input
          disabled={disabled}
          id={id}
          {...rest}
          aria-describedby={showMessage ? messageId : rest["aria-describedby"]}
          aria-invalid={hasError || undefined}
          className={input({
            className,
            error: hasError,
            left: Boolean(left),
            right: Boolean(right),
          })}
        />

        {right ? (
          /* Interactive affixes (a reveal-password toggle) need their own pointer events. */
          <div className={affix({ className: "right-0 pr-3 [&:has(button)]:pointer-events-auto" })}>
            <IconWrapper size="sm">{right}</IconWrapper>
          </div>
        ) : null}
      </div>

      <Message error={error} id={messageId} message={message} reserveSpace={!compact} />
    </div>
  );
};
